import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { Comments as CommentsReport } from "apps/company/api/rest/company-dashboard-engagement";
import { useTranslation } from "lib/i18n/use-translation";

import engagementStyles from "../engagement/engagement.module.scss";
import styles from "./engagement-redesign.module.scss";

const K = "admin:companyDashboard.engagement";

// Safari drops a list's role once its markers are styled away.
const LIST = "list";

// The gap between cards.
const GAP_PX = 16;

const Chevron = ({ points }: { points: string }) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points={points} />
  </svg>
);

type Edges = { fits: boolean; atStart: boolean; atEnd: boolean };

// Where the track is scrolled to, so the buttons can say which way is left
// to go, and leave when every card fits.
const useEdges = (comments: CommentsReport) => {
  const track = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<Edges>({
    fits: true,
    atStart: true,
    atEnd: true,
  });
  const measure = useCallback(() => {
    const element = track.current;
    if (!element) return;
    const { scrollLeft, scrollWidth, clientWidth } = element;
    setEdges({
      fits: scrollWidth <= clientWidth,
      atStart: scrollLeft <= 0,
      atEnd: scrollLeft + clientWidth >= scrollWidth - 1,
    });
  }, []);
  // The track can change width without the window doing so, as fonts load
  // or the layout around it moves.
  useEffect(() => {
    measure();
    const element = track.current;
    const observer =
      element && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    if (element) observer?.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, comments]);
  return { track, edges, measure };
};

// Four lines of a comment, the rest scrolled to. Only a comment that runs
// longer is a stop for the keyboard, so it can be scrolled without a mouse.
// Measured again whenever the text's box resizes, as the screen turns or the
// web font arrives and rewraps it.
const CommentText = ({ text }: { text: string }) => {
  const element = useRef<HTMLParagraphElement>(null);
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const paragraph = element.current;
    if (!paragraph) return;
    let mounted = true;
    const measure = () => {
      // A pixel either way is rounding at fractional zoom, not hidden text.
      if (mounted)
        setClipped(paragraph.scrollHeight - paragraph.clientHeight > 1);
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    observer?.observe(paragraph);
    document.fonts?.ready.then(measure);
    return () => {
      mounted = false;
      observer?.disconnect();
    };
  }, [text]);
  return (
    <p
      ref={element}
      className={styles["comment-text"]}
      tabIndex={clipped ? 0 : undefined}
    >
      <q>{text}</q>
    </p>
  );
};

// What employees wrote, as they wrote it, with nothing about who wrote it:
// every comment is signed the same way.
const Comments = ({
  comments,
  day,
}: {
  comments: CommentsReport;
  day: string;
}) => {
  const { t } = useTranslation(["admin"]);
  const headingId = useId();
  const trackId = useId();
  const { track, edges, measure } = useEdges(comments);
  // One card at a time, however wide the cards are on this screen.
  const scroll = (direction: 1 | -1) => {
    const card = track.current?.querySelector("li");
    if (!track.current || !card) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.current.scrollBy({
      left: direction * (card.offsetWidth + GAP_PX),
      behavior: still ? "auto" : "smooth",
    });
  };

  return (
    <section className={styles["comments"]} aria-labelledby={headingId}>
      <div className={styles["comments-head"]}>
        <div>
          <h4 id={headingId} className={engagementStyles["card-title"]}>
            {t(`${K}.comments.title`)}
          </h4>
          <p className={styles["comments-description"]}>
            {t(`${K}.comments.description`, { date: day })}
          </p>
        </div>
        {!comments.suppressed && !edges.fits && (
          <div className={styles["comments-scroll"]}>
            <button
              type="button"
              aria-label={t(`${K}.comments.scrollLeft`)}
              aria-controls={trackId}
              disabled={edges.atStart}
              onClick={() => scroll(-1)}
            >
              <Chevron points="15 18 9 12 15 6" />
            </button>
            <button
              type="button"
              aria-label={t(`${K}.comments.scrollRight`)}
              aria-controls={trackId}
              disabled={edges.atEnd}
              onClick={() => scroll(1)}
            >
              <Chevron points="9 18 15 12 9 6" />
            </button>
          </div>
        )}
      </div>
      {comments.suppressed ? (
        <p className={engagementStyles["not-enough"]}>
          {t(`${K}.comments.notEnough`)}
        </p>
      ) : (
        <div
          id={trackId}
          ref={track}
          className={styles["comments-track"]}
          role="group"
          aria-labelledby={headingId}
          tabIndex={0}
          onScroll={measure}
        >
          <ul className={styles["comments-list"]} role={LIST}>
            {comments.value.map((comment, index) => (
              <li key={index} className={styles["comment"]}>
                <CommentText text={comment.text} />
                <p className={styles["comment-by"]}>
                  {t(`${K}.comments.attribution`)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default Comments;
